import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NivaLogo from '../components/NivaLogo';
import SlideWelcome from '../components/onboarding/SlideWelcome';
import SlidePatients from '../components/onboarding/SlidePatients';
import SlideRecording from '../components/onboarding/SlideRecording';
import SlideReview from '../components/onboarding/SlideReview';
import SlideTasks from '../components/onboarding/SlideTasks';
import SlideTabs from '../components/onboarding/SlideTabs';
import SlideHandover from '../components/onboarding/SlideHandover';
import SlideProfile from '../components/onboarding/SlideProfile';
import SlideReady from '../components/onboarding/SlideReady';
import { ROUTES } from '../constants/routes';
import {
  markTutorialCompleted,
  TUTORIAL_SLIDE_COUNT,
} from '../utils/tutorialStorage';

const SLIDES = [
  SlideWelcome,
  SlidePatients,
  SlideRecording,
  SlideReview,
  SlideTasks,
  SlideTabs,
  SlideHandover,
  SlideProfile,
  SlideReady,
];

export default function OnboardingScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromProfile = Boolean(location.state?.fromProfile);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  const isLastSlide = currentSlide === TUTORIAL_SLIDE_COUNT - 1;
  const isFirstSlide = currentSlide === 0;
  const CurrentSlideComponent = SLIDES[currentSlide];

  const finishTutorial = useCallback(() => {
    markTutorialCompleted();
    navigate(fromProfile ? ROUTES.PROFILE : ROUTES.PATIENTS, { replace: true });
  }, [fromProfile, navigate]);

  const goNext = () => {
    if (isLastSlide) {
      finishTutorial();
      return;
    }
    setDirection('forward');
    setCurrentSlide((s) => s + 1);
  };

  const goBack = () => {
    if (isFirstSlide) return;
    setDirection('back');
    setCurrentSlide((s) => s - 1);
  };

  const handleSkipConfirm = () => {
    setShowSkipConfirm(false);
    finishTutorial();
  };

  return (
    <div className="fixed inset-0 z-[100] flex min-h-[100dvh] flex-col bg-white">
      {/* Header */}
      <header className="shrink-0 border-b border-gray-100 px-3 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-4 sm:pb-3 sm:pt-4">
        <div className="flex items-center justify-between gap-2">
          <NivaLogo variant="header" />
          <button
            type="button"
            onClick={() => setShowSkipConfirm(true)}
            className="min-h-touch shrink-0 rounded-lg px-2 text-sm font-medium text-gray-500"
          >
            Skip
          </button>
        </div>
        <div className="mt-2 flex items-center justify-center gap-1 sm:mt-4 sm:gap-1.5">
          {SLIDES.map((_, index) => (
            <div
              key={index}
              className={`h-1.5 rounded-full transition-all duration-300 ease-out sm:h-2 ${
                index === currentSlide
                  ? 'w-5 bg-[#1E40AF] sm:w-6'
                  : 'w-1.5 bg-[#E5E7EB] sm:w-2'
              }`}
            />
          ))}
        </div>
      </header>

      {/* Slide content */}
      <div className="onboarding-slide-scroll min-h-0 flex-1 px-3 py-3 sm:px-4 sm:py-4">
        <div
          key={currentSlide}
          className={`min-h-full ${
            direction === 'forward' ? 'onboarding-slide-forward' : 'onboarding-slide-back'
          }`}
        >
          <CurrentSlideComponent />
        </div>
      </div>

      {/* Footer */}
      <footer className="shrink-0 border-t border-gray-100 px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-4 sm:py-4">
        {isLastSlide ? (
          <button
            type="button"
            onClick={finishTutorial}
            className="min-h-touch w-full rounded-xl bg-success py-3.5 text-base font-semibold text-white shadow-sm"
          >
            Get Started
          </button>
        ) : (
          <div className="flex items-stretch gap-2 sm:gap-3">
            {!isFirstSlide ? (
              <button
                type="button"
                onClick={goBack}
                className="min-h-touch shrink-0 rounded-xl border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-600 sm:px-6"
              >
                Back
              </button>
            ) : null}
            <button
              type="button"
              onClick={goNext}
              className="min-h-touch flex-1 rounded-xl bg-[#1E40AF] px-6 py-3 text-sm font-semibold text-white shadow-sm sm:ml-auto sm:flex-none sm:px-8"
            >
              Next
            </button>
          </div>
        )}
      </footer>

      {/* Skip confirmation */}
      {showSkipConfirm && (
        <div className="fixed inset-0 z-[110] flex items-end justify-center bg-black/40 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:items-center sm:px-6">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl sm:p-6">
            <p className="mb-6 text-center text-sm leading-relaxed text-gray-700">
              Are you sure you want to skip the tutorial? You can view it again from your profile.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setShowSkipConfirm(false)}
                className="rounded-xl bg-[#1E40AF] py-3 text-sm font-semibold text-white"
              >
                Continue tutorial
              </button>
              <button
                type="button"
                onClick={handleSkipConfirm}
                className="rounded-xl border border-gray-300 py-3 text-sm font-semibold text-gray-600"
              >
                Skip anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
